import React, { useEffect, useRef, useState } from 'react'
import { makeStyles, Button, Grid, InputField } from '@paintscout/ui'
import { TimeSelect, DealSelect, EstimatorSelect, SingleDatePicker, Editor, useDialogs } from '@paintscout/ui-business'
import type { DropdownSelectOption } from '@paintscout/ui-business'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import type { Deal, Task, User } from '@paintscout/api'
import { useDealQuery } from '@paintscout/api'
import type { DashboardEstimator } from '@paintscout/util/builder'
import { addAuth0Prefix, removeAuth0Prefix, getHighestRole } from '@paintscout/util'
import { resolvePermissions } from '@paintscout/util/builder'
import type { AppMetadata } from 'paintscout'
import { useConnection } from 'react-detect-offline'
import type { Moment } from 'moment'
import moment from 'moment'
import AddIcon from '@material-ui/icons/Add'
import { goToDeal } from 'src/util/routing'
import classNames from 'classnames'
import { DealDialog } from '../dialogs'
import { useTaskValues } from './useTaskValues'

export function canAssignTaskToUser(
  user: User,
  dealAssignedUsers?: Record<string, { assigned?: boolean }>,
  dealOwnerId?: string
) {
  const role = getHighestRole(user.app_metadata?.roles ?? [])

  if (['team', 'admin', 'viewer'].includes(role)) {
    return true
  }

  const permissions = resolvePermissions(user.app_metadata as AppMetadata)

  // TODO: should there be deal assignment for painters / crews? Right now painters can be assign tasks on any deal, not just their 'assigned' deals.
  if (role === 'painter' && permissions.includes('contribute:assigned-deals')) return true

  if (role === 'core') {
    const userId = removeAuth0Prefix(user.user_id)
    return (
      dealAssignedUsers?.[userId]?.assigned === true ||
      dealOwnerId === userId ||
      permissions.includes('write:all-tasks')
    )
  }

  return false
}

type FormProps = JSX.IntrinsicElements['form']

export interface TaskFormProps extends Omit<FormProps, 'onSubmit'> {
  classes?: StyleClasses<typeof useStyles>
  task?: Partial<Task>
  disabled?: boolean
  disableCompanySelector?: boolean
  isSecondary?: boolean
  hideDeal?: boolean
  hideUser?: boolean
  defaultEstimator?: string
  onSubmit: (task: Task) => any
  dealQuery?: string // lucene query for deals
  searchCompanyId?: string // specify company id to search deals for
  isNew?: boolean
  onDialogBack?: (event: React.MouseEvent<HTMLElement>) => void
  onDialogCancel?: (event: React.MouseEvent<HTMLElement>) => void
  onEdit?: (task: Task) => void
}

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {},
    selectField: { marginTop: theme.spacing() },
    estimatorSelect: {
      width: '100%'
    },
    editButton: {
      alignSelf: 'start',
      marginTop: 20,
      [theme.breakpoints.down('md')]: {
        marginTop: 22
      }
    },
    disabledButton: {
      outline: `1px solid ${theme.paintscout.palette.brand.Neutral.P80}`
    },
    textField: {
      color: theme.palette.text.primary
    },
    error: {
      position: 'absolute'
    },
    editorDiv: {
      marginBottom: theme.spacing(3)
    },
    datePickerDialogContent: {
      display: 'flex',
      justifyContent: 'center'
    }
  }),
  { name: 'TaskForm' }
)

function TaskForm({
  task,
  disabled,
  onSubmit,
  hideDeal,
  hideUser,
  defaultEstimator,
  searchCompanyId,
  dealQuery,
  isNew,
  onDialogBack,
  onDialogCancel,
  onEdit,
  ...baseProps
}: TaskFormProps) {
  const classes = useStyles({})
  const { online } = useConnection()
  const {
    handleDateChange,
    handleDealChange,
    handleFieldChange,
    handleOwnerChange,
    handleTimeChange,
    handleNotesChange,
    setTaskValues,
    taskValues
  } = useTaskValues({ task, defaultEstimator })
  const [titleTooLong, setTitleTooLong] = useState(false)
  const [dealOpen, setDealOpen] = useState(false)
  const dealOpenRef = useRef(false)
  const { openDialog, dismissDialog, dismissAllDialogs } = useDialogs()
  const { data: dealData } = useDealQuery({
    variables: { id: taskValues?.dealId },
    skip: !taskValues?.dealId,
    fetchPolicy: 'cache-first'
  })
  const dealAssignedUsers = dealData?.deal?.assignedUsers as Record<string, { assigned?: boolean }> | undefined
  const dealOwnerId = dealData?.deal?.owner?.id

  async function handleSubmit(event: React.FormEvent<HTMLElement>) {
    event.preventDefault()

    // merge taskValues and omit falsey taskValues
    const newTask: Task = {
      ...taskValues,
      updated: Date.now()
    }

    Object.keys(newTask).forEach((key) => {
      if (typeof newTask[key] !== 'string') {
        return
      }

      newTask[key] = newTask[key].trim()
    })

    onSubmit(newTask)
  }
  const commonInputProps = {
    onChange: handleFieldChange,
    disabled,
    fullWidth: true,
    classes: { root: classes.textField }
  }

  useEffect(() => {
    onEdit?.(taskValues)
  }, [taskValues])

  const handleDealButtonPress = (event: React.SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()
    const shouldCreateNew = dealOpenRef.current || !taskValues.dealId
    openDialog(DealDialog, {
      dealId: shouldCreateNew ? null : taskValues.dealId,
      onConfirm: (_deal: Deal) => {
        setTaskValues({
          ...taskValues,
          dealId: _deal._id,
          dealName: _deal.name
        })
        dismissDialog()
      },
      onBack: onDialogBack ?? dismissDialog,
      onCancel: onDialogCancel ?? dismissAllDialogs
    })
  }

  return (
    <form className={classes.root} autoComplete="off" onSubmit={handleSubmit} {...baseProps}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={hideDeal ? 6 : 12}>
          <InputField
            {...commonInputProps}
            className="tour-task-form-title-field"
            required={true}
            error={!taskValues.title || taskValues?.title?.length > 50}
            pattern={{ expression: '(.*[A-Za-z]+.*)', title: 'Name' }}
            id="title"
            label="Title"
            autoComplete={'off'}
            value={taskValues.title}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const value = event.target.value
              setTitleTooLong(value.length > 50)
              handleFieldChange(event)
            }}
            helperText={titleTooLong ? 'Task title cannot be longer than 50 characters.' : ''}
            showErrorBeforeValidation={taskValues?.title?.length > 50}
          />
        </Grid>
        {!hideDeal && (
          <Grid item xs={12} md={6}>
            <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '18px', alignItems: 'center' }}>
              <div style={{ width: '100%' }}>
                <DealSelect
                  className="tour-task-form-deal-field"
                  query={dealQuery || ''}
                  name="dealId"
                  label="Deal"
                  fullWidth
                  variant="single"
                  required
                  nameValue={taskValues?.dealName || ''}
                  error={!taskValues?.dealId}
                  searchable
                  searchByName
                  disablePortal={false}
                  searchCompanyId={searchCompanyId}
                  disabled={disabled || !online || !isNew}
                  hideIcon={!isNew}
                  value={taskValues?.dealId ?? null}
                  onChange={(dealOption: DropdownSelectOption) => {
                    handleDealChange(dealOption)
                  }}
                  onFocus={() => {
                    setDealOpen(true)
                    dealOpenRef.current = true
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      setDealOpen(false)
                      dealOpenRef.current = false
                    }, 0)
                  }}
                />
              </div>
              {isNew ? (
                <>
                  <Button
                    variant="dialog"
                    color="primary"
                    inputHeight
                    className={classes.editButton}
                    startIcon={!taskValues.dealId || dealOpen ? <AddIcon /> : null}
                    onMouseDown={handleDealButtonPress}
                    onTouchStart={handleDealButtonPress}
                    onPointerDown={handleDealButtonPress}
                    onClick={handleDealButtonPress}
                  >
                    {taskValues.dealId && !dealOpen ? 'Edit' : 'New'}
                  </Button>
                </>
              ) : (
                <Button
                  variant="dialog"
                  color="primary"
                  inputHeight
                  className={classes.editButton}
                  startIcon={null}
                  onClick={() => {
                    goToDeal(taskValues.dealId)
                  }}
                >
                  View Deal
                </Button>
              )}
            </div>
          </Grid>
        )}
        {!hideUser && (
          <Grid item xs={12} md={6}>
            <EstimatorSelect
              key={taskValues?.dealId ?? 'no-deal'}
              name="estimators"
              label="User"
              className={classNames(classes.estimatorSelect, 'tour-task-form-owner-field')}
              fullWidth
              variant="single"
              searchable
              value={taskValues?.owner?.id ? addAuth0Prefix(taskValues.owner.id) : null}
              disabled={disabled || !online || (!hideDeal && !taskValues?.dealId)}
              includeUser={(user) => canAssignTaskToUser(user, dealAssignedUsers, dealOwnerId)}
              onChange={async (estimatorOption: DashboardEstimator) => {
                handleOwnerChange(estimatorOption)
              }}
              onFetched={(estimatorOptions: DashboardEstimator[]) => {
                if (taskValues?.owner?.id) return
                if (defaultEstimator) {
                  const fullEstimator = estimatorOptions.find(
                    (estimator) => estimator.value === addAuth0Prefix(defaultEstimator)
                  )
                  if (fullEstimator) {
                    handleOwnerChange(fullEstimator)
                  }
                } else if (estimatorOptions.length === 1) {
                  handleOwnerChange(estimatorOptions[0])
                }
              }}
              hasUnselect
              autoApply
            />
          </Grid>
        )}
        <Grid item xs={12} className={classes.editorDiv}>
          <Editor
            classes={{ root: 'tour-task-form-description-field' }}
            disabled={disabled}
            label={'Description'}
            content={taskValues?.notes}
            fullWidth={true}
            debounce={400}
            onChange={(value) => {
              handleNotesChange(value)
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} className="tour-task-form-due-date-field">
          <SingleDatePicker
            classes={{ dialogContent: classes.datePickerDialogContent }}
            label="Due Date"
            date={taskValues?.startDateTime ? moment(taskValues.startDateTime) : null}
            disabled={disabled}
            clearable
            onChange={(newDate: Moment) => {
              handleDateChange(newDate ? newDate.toDate() : null)
            }}
            PopperProps={{
              placement: 'bottom-end',
              modifiers: {
                preventOverflow: {
                  enabled: false
                }
              }
            }}
            modalBreakpoint={'xl'}
          />
        </Grid>
        <Grid item xs={12} md={6} className="tour-task-form-due-time-field">
          <TimeSelect
            onChange={(time: DropdownSelectOption) => {
              handleTimeChange(time.value)
            }}
            value={taskValues.startDateTime}
            variant={'single'}
            label="Time"
            disabled={disabled || !taskValues.startDateTime}
            startAt={6}
          />
        </Grid>
      </Grid>
    </form>
  )
}

export default TaskForm
